// ============================================================================
// Ask AI's missed-shift card — what it shows and its save (item #982, the Time
// & Labor part; TCSD-0531 one tool per part, saved through the screen's own
// function). The server half is supabase/functions/am-copilot/time.ts.
//
// The save is manualSegment() — the very function Timecards' "Record a shift
// manually" calls (rpc_time_manual_segment, TCSD-0553). Times are read on the
// BUILDING's clock through siteInputToISO, the one way in (Ben, 2026-09-24); an
// out time earlier than the in time ends the next morning.
// ============================================================================

import { manualSegment } from '../time-labor/hooks/use-time-data';
import { siteInputToISO } from '../../lib/siteTime';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { addDays, crossesMidnight, shiftHours } from '../../../supabase/functions/am-copilot/time';

/** A draft as the server persisted it (prepareTimeAction). */
export interface TimeDraft {
  action: 'record_shift';
  worker_id: string;
  facility_id: string;
  day: string;
  in_time: string;
  out_time: string;
  reason?: string;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

/** What the person can change on the card. `tz` is the chosen building's clock. */
export interface TimeFields {
  worker_id: string;
  facility_id: string;
  service_line_code: string;
  day: string;
  in_time: string;
  out_time: string;
  reason: string;
  tz: string | null;
}

export const fieldsFromDraft = (d: TimeDraft): TimeFields => ({
  worker_id: d.worker_id,
  facility_id: d.facility_id,
  // The Timecards form's own default line.
  service_line_code: 'evs',
  day: d.day,
  in_time: d.in_time,
  out_time: d.out_time,
  reason: d.reason ?? '',
  tz: typeof d.context?.tz === 'string' ? (d.context.tz as string) : null,
});

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Why the button is off, in words, or null. */
export function timeMissing(f: TimeFields): string | null {
  if (!f.worker_id) return 'Pick the worker.';
  if (!f.facility_id) return 'Pick the building.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.day)) return 'Fill in the day the shift started.';
  if (!HHMM.test(f.in_time) || !HHMM.test(f.out_time)) return 'Fill in both times.';
  if (f.in_time === f.out_time) return 'The in and out times are the same.';
  if (!f.tz) return "The building's time zone is still loading.";
  return null;
}

/** The in and out instants, on the building's clock. Out before in = the next morning. */
export function shiftInstants(f: TimeFields): { startedAt: string; endedAt: string } | null {
  if (!f.tz || timeMissing(f)) return null;
  const outDay = crossesMidnight(f.in_time, f.out_time) ? addDays(f.day, 1) : f.day;
  const startedAt = siteInputToISO(`${f.day}T${f.in_time}`, f.tz);
  const endedAt = siteInputToISO(`${outDay}T${f.out_time}`, f.tz);
  return startedAt && endedAt ? { startedAt, endedAt } : null;
}

export const hoursOf = (f: TimeFields) => shiftHours(f.in_time, f.out_time);

export interface TimeCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}

/** Save the shift through the Timecards screen's own function. */
export async function commitTimeAction(f: TimeFields): Promise<TimeCommitResult> {
  const missing = timeMissing(f);
  if (missing) return { ok: false, error: missing };
  const at = shiftInstants(f);
  if (!at) return { ok: false, error: 'The times could not be read on the building’s clock.' };
  try {
    const r = await manualSegment({
      workerId: f.worker_id,
      startedAt: at.startedAt,
      endedAt: at.endedAt,
      reason: f.reason.trim(),
      facilityId: f.facility_id,
      marketRegion: null,
      serviceLineCode: f.service_line_code || null,
      shiftCode: null,
    });
    return {
      ok: true,
      note: `Recorded · ${(r.raw_minutes / 60).toFixed(2)} h. Your name is on both punches.`,
      target_table: 'shift_segments',
      target_row_id: r.segment_id,
      committed_payload: { worker_id: f.worker_id, facility_id: f.facility_id, started_at: at.startedAt, ended_at: at.endedAt, reason: f.reason.trim() },
    };
  } catch (e) {
    return { ok: false, error: rpcErrorMessage(e, 'That did not save.') };
  }
}
